// src/studio/components/GroupSectionView.jsx
//
// A GROUP section (SectionKind.GROUP) is several artifacts sharing one
// section key — one per requirement for the SFDC Solution Design's
// `requirement_designs` (object_name, functional_flow, data_dictionary,
// components, design_visual, ...), one per program for the Technical
// Design's `programs` (a completely different 21-field shape — program_name,
// interface_category, integration_methodology, ...). Each artifact's
// content is a JSON object, not a flat row.
//
// Renders whichever fields the item actually has, rather than a fixed list
// of SFDC's own field names (the previous version's hardcoded object_name/
// functional_flow/data_dictionary/components/design_visual list silently
// rendered nothing for a `programs` item, whose fields never matched any of
// those names) — this is what "the output is generated but not mapped in
// the frontend" looked like from the API's own response. A nested
// list-of-dicts field (data_dictionary, components, interfaces, ...) goes
// through TableSectionView the same way a top-level TABLE section does; a
// plain string goes through MarkdownView; anything else renders as a
// simple label/value line. New document types with their own GROUP shape
// need no frontend change to display correctly.
import { useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronRight } from "lucide-react";
import ArtifactEditor from "./ArtifactEditor";
import MarkdownView from "./MarkdownView";
import TableSectionView from "./TableSectionView";

function parseItem(raw) {
  if (!raw || !raw.trim()) return null;
  let text = raw.trim();
  if (text.startsWith("```")) {
    text = text.split("\n").filter((l) => !l.trim().startsWith("```")).join("\n").trim();
  }
  try {
    return JSON.parse(text);
  } catch {
    return undefined; // signals unparseable, distinct from "not generated"
  }
}

function labelFromGroupKey(groupKey) {
  // "requirement:REQ-001" -> "REQ-001"
  if (!groupKey) return "Item";
  const idx = groupKey.indexOf(":");
  return idx === -1 ? groupKey : groupKey.slice(idx + 1);
}

function humanizeKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// The first short-ish string field, for the collapsed row's preview snippet
// — whatever that field is called (design_statement for a requirement
// design, program_description for a program), not a hardcoded name.
function previewText(item) {
  if (!item) return null;
  for (const value of Object.values(item)) {
    if (typeof value === "string" && value.trim() && value.length <= 160) {
      return value;
    }
  }
  return null;
}

function isRowList(value) {
  return Array.isArray(value) && value.length > 0 && value.every((v) => v && typeof v === "object");
}

function GroupItemField({ fieldKey, value }) {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value) && value.length === 0) return null;

  const heading = (
    <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1">
      {humanizeKey(fieldKey)}
    </p>
  );

  if (isRowList(value)) {
    return (
      <div>
        {heading}
        <TableSectionView content={JSON.stringify(value)} />
      </div>
    );
  }

  if (typeof value === "string") {
    return (
      <div>
        {heading}
        <MarkdownView content={value} />
      </div>
    );
  }

  return (
    <p className="text-sm text-slate-500">
      <span className="font-semibold text-slate-700">{humanizeKey(fieldKey)}:</span> {String(value)}
    </p>
  );
}

GroupItemField.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  // Shape varies per document type (a string, a nested list of dicts, a
  // number, ...) -- see the file header for why this stays untyped.
  value: PropTypes.any,
};

function GroupItemCard({ artifact, runId, sectionKey, disabled, onSaved }) {
  const [open, setOpen] = useState(false);
  const label = labelFromGroupKey(artifact.group_key);
  const item = parseItem(artifact.content_markdown);
  const preview = previewText(item);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        <span className="text-base font-semibold text-slate-800">{label}</span>
        {preview && (
          <span className="text-sm text-slate-400 truncate flex-1 min-w-0">{preview}</span>
        )}
      </button>

      {open && (
        <div className="p-4">
          {item === undefined ? (
            <p className="text-base text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              This item&rsquo;s content isn&rsquo;t valid — try regenerating the document.
            </p>
          ) : item === null || Object.keys(item).length === 0 ? (
            <p className="text-base text-slate-400 italic">Nothing generated for this item yet.</p>
          ) : (
            <ArtifactEditor
              runId={runId}
              sectionKey={sectionKey}
              groupKey={artifact.group_key}
              contentMarkdown={artifact.content_markdown}
              isJson
              disabled={disabled}
              onSaved={onSaved}
            >
              <div className="space-y-4">
                {Object.entries(item).map(([fieldKey, value]) => (
                  <GroupItemField key={fieldKey} fieldKey={fieldKey} value={value} />
                ))}
              </div>
            </ArtifactEditor>
          )}
        </div>
      )}
    </div>
  );
}

GroupItemCard.propTypes = {
  artifact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    group_key: PropTypes.string,
    content_markdown: PropTypes.string,
  }).isRequired,
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sectionKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onSaved: PropTypes.func.isRequired,
};

export default function GroupSectionView({ artifacts, runId, sectionKey, disabled = false, onItemSaved }) {
  if (!artifacts || artifacts.length === 0) {
    return <p className="text-base text-slate-400 italic">No items generated for this section yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {artifacts.map((artifact) => (
        <GroupItemCard
          key={artifact.id}
          artifact={artifact}
          runId={runId}
          sectionKey={sectionKey}
          disabled={disabled}
          onSaved={onItemSaved}
        />
      ))}
    </div>
  );
}

GroupSectionView.propTypes = {
  artifacts: PropTypes.arrayOf(PropTypes.object),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sectionKey: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onItemSaved: PropTypes.func.isRequired,
};
